import { useCallback, useReducer } from 'react';

export type ApiCheckResponseDto = {
  requestId: string;
  subject: string;
  email?: string;
  issuer?: string;
  issuedAt?: number;
  expiresAt?: number;
};

export type ApiCheckError = {
  kind: 'UNAUTHORIZED' | 'FORBIDDEN' | 'NETWORK' | 'UNKNOWN';
  message: string;
  status?: number;
};

export type UseApiCheckState = {
  isLoading: boolean;
  data: ApiCheckResponseDto | null;
  error: ApiCheckError | null;
  lastRunAt?: number;
};

type Action =
  | { type: 'START' }
  | { type: 'SUCCESS'; payload: ApiCheckResponseDto }
  | { type: 'FAILURE'; payload: ApiCheckError }
  | { type: 'RESET' };

function reducer(state: UseApiCheckState, action: Action): UseApiCheckState {
  switch (action.type) {
    case 'START':
      return { isLoading: true, data: null, error: null };
    case 'SUCCESS':
      return { isLoading: false, data: action.payload, error: null, lastRunAt: Date.now() };
    case 'FAILURE':
      return { isLoading: false, data: null, error: action.payload, lastRunAt: Date.now() };
    case 'RESET':
      return { isLoading: false, data: null, error: null };
    default:
      return state;
  }
}

const initialState: UseApiCheckState = {
  isLoading: false,
  data: null,
  error: null,
};

/**
 * Per IF45 spec: apiClient.getAuthCheck returns the bare DTO directly.
 * On HTTP errors, the adapter at the call site must throw an error with an
 * optional `status` property so the hook can categorise it correctly.
 */
interface UseApiCheckParams {
  apiClient: { getAuthCheck: (idToken: string) => Promise<ApiCheckResponseDto> };
  getIdToken: () => string | null;
  onAuthExpired?: () => void;
}

export function useApiCheck(params: UseApiCheckParams): {
  state: UseApiCheckState;
  run: () => Promise<void>;
  reset: () => void;
} {
  const [state, dispatch] = useReducer(reducer, initialState);

  const run = useCallback(async () => {
    dispatch({ type: 'START' });
    const token = params.getIdToken();
    if (!token) {
      dispatch({
        type: 'FAILURE',
        payload: { kind: 'UNAUTHORIZED', message: 'No authentication token available' },
      });
      return;
    }
    try {
      const data = await params.apiClient.getAuthCheck(token);
      dispatch({ type: 'SUCCESS', payload: data });
    } catch (e) {
      // Adapters may attach a numeric `status` to the thrown Error so the hook
      // can map HTTP status codes to specific error kinds.
      const status = (e as { status?: number }).status;
      let kind: ApiCheckError['kind'] = 'UNKNOWN';
      if (status === 401) kind = 'UNAUTHORIZED';
      else if (status === 403) kind = 'FORBIDDEN';
      else if (status === 0) kind = 'NETWORK';

      if (status === 401 || status === 403) {
        params.onAuthExpired?.();
      }

      dispatch({
        type: 'FAILURE',
        payload: {
          kind,
          message: e instanceof Error ? e.message : 'Unexpected error during API check',
          status,
        },
      });
    }
  }, [params]);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return { state, run, reset };
}
